import React from 'react';
import { 
  Typography, 
  Box,
  Chip,
  Alert,
  AlertTitle,
  Skeleton
} from '@mui/material';
import InsightCard from './shared/InsightCard';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

interface RecommendationCardProps {
  data: {
    nextVisitPrediction: string;
    suggestedServices: string[];
    riskLevel: 'low' | 'medium' | 'high';
  } | null;
  loading?: boolean;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({ data, loading = false }) => {
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high':
        return { bg: '#ffebee', color: '#c62828', label: '高リスク' };
      case 'medium':
        return { bg: '#fff3e0', color: '#ef6c00', label: '中リスク' };
      default:
        return { bg: '#e8f5e9', color: '#2e7d32', label: '低リスク' };
    }
  };

  const getRiskIcon = (risk: string) => {
    if (risk === 'high') {
      return <WarningAmberIcon sx={{ color: '#c62828' }} />;
    }
    return null;
  };

  if (loading) {
    return (
      <InsightCard
        title="レコメンド・アクション"
        cardVariant="default"
      >
        <Skeleton variant="text" width="70%" height={32} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="50%" height={24} sx={{ mt: 2 }} />
      </InsightCard>
    );
  }

  if (!data) {
    return (
      <InsightCard
        title="レコメンド・アクション"
        cardVariant="default"
      >
        <Typography color="text.secondary">
          データがありません
        </Typography>
      </InsightCard>
    );
  }

  const riskInfo = getRiskColor(data.riskLevel);

  return (
    <InsightCard
      title="レコメンド・アクション"
      cardVariant="default"
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getRiskIcon(data.riskLevel)}
          <Chip
            label={riskInfo.label}
            size="small"
            sx={{
              bgcolor: riskInfo.bg,
              color: riskInfo.color,
              fontWeight: 'bold'
            }}
          />
        </Box>
      }
    >

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScheduleIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              次回来店予測
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
            {data.nextVisitPrediction}
          </Typography>
        </Box>

        {data.riskLevel === 'high' && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<WarningAmberIcon />}
          >
            <AlertTitle>離脱リスクあり</AlertTitle>
            前回来店から長期間経過しています。フォローアップを推奨します。
          </Alert>
        )}

        {data.riskLevel === 'medium' && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            icon={<InfoOutlinedIcon />}
          >
            通常より来店間隔が空いています。連絡を検討してください。
          </Alert>
        )}

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LightbulbOutlinedIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              おすすめサービス
            </Typography>
          </Box>
          {data.suggestedServices && data.suggestedServices.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {data.suggestedServices.map((service, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'secondary.50',
                    border: '1px solid rgba(156, 39, 176, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'secondary.dark' }}>
                    {index + 1}. {service}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Alert 
              severity="info" 
              sx={{ 
                bgcolor: 'secondary.50',
                color: 'secondary.dark',
                '& .MuiAlert-icon': {
                  color: 'secondary.main'
                }
              }}
            >
              <Typography variant="body2">
                サービス履歴を分析中です
              </Typography>
            </Alert>
          )}
        </Box>
    </InsightCard>
  );
};

export default RecommendationCard;